import { FETCH_STATUS } from "@/shared/api";
import { BackendSource } from "@/shared/api/firehose";
import {
  BenzingaNewsStory,
  CryptoScraperStory,
  FinancialReportsEUStory,
  FlyStory,
  NewsFeedItem,
  TrumpTruthStory,
} from "@/shared/api/firehose/models";
import { create } from "zustand";

/** Maximum number of items to keep per source array */
const MAX_PER_SOURCE = 200;
/** Maximum number of items to keep in the allStories aggregate */
const MAX_ALL_STORIES = 500;

/**
 * Mapping of BackendSource to the corresponding field in the FirehoseStore.
 */
// TODO:: add fields for other story types when creating widgets for them
// TODO:: there may be dummy mappings here, remove them when adding actual widgets
export const sourceToFieldMap: Record<BackendSource, keyof FirehoseStore> = {
  "stocks-one": "flyStories",
  "stocks-two": "benzingaStories",
  "stocks-three": "benzingaStories",
  "crypto-web-news": "cryptoWebStories",
  "fin-web-news": "generalWebStories",
  "energy-web-news": "energyWebStories",
  "truth-social": "truthSocialStories",
  "energy-x": "energyXStories",
  "fin-x": "finXStories",
  "stocks-news": "stocksNewsStories",

  "stocks-four": "tradeXchangeStories",
  "stocks-four-filings": "tradeXchangeStories",
  "stocks-four-fedspeak": "tradeXchangeStories",
  "stocks-four-general": "tradeXchangeStories",

  "newsquawk-sec-filings": "secFilingsStories",
  "stocks-five-eu": "financialReportsEUStories",
};

/**
 * Type definition for the FirehoseStore.
 * This store manages different types of news stories and their fetch status.
 */
type FirehoseStore = {
  /** Current fetch status of the news stories */
  status: FETCH_STATUS;

  /** Array of all news stories */
  allStories: NewsFeedItem[];

  /** Array of Fly news stories */
  flyStories: FlyStory[];

  /** Array of Benzinga news stories */
  benzingaStories: BenzingaNewsStory[];

  /** Array of Crypto Blogs & Websites Stories */
  cryptoWebStories: CryptoScraperStory[];

  /** Array of Energy Blogs & Websites Stories */
  energyWebStories: NewsFeedItem[];

  /** Array of Energy X (Twitter) Stories */
  energyXStories: NewsFeedItem[];

  /** Array of stocks news stories */
  stocksNewsStories: NewsFeedItem[];

  /** Array of stocks news stories */
  finXStories: NewsFeedItem[];

  /** Array of general web stories */
  generalWebStories: NewsFeedItem[];

  // TODO:: add fields for other story types when creating widgets for them

  /** Array of TruthSocial stories */
  truthSocialStories: TrumpTruthStory[];

  tradeXchangeStories: NewsFeedItem[];

  secFilingsStories: NewsFeedItem[];

  /** Array of Financial Reports EU stories */
  financialReportsEUStories: FinancialReportsEUStory[];

  setStatus: (status: FETCH_STATUS) => void;

  /**
   * Adds a single news story to the store.
   * This will add the story to the `allStories` and the appropriate field based on its backend source.
   * @param story - The news story to add.
   */
  addStory: (story: NewsFeedItem) => void;

  pushStories: (stories: NewsFeedItem[]) => void;

  /**
   * Sets the `allStories` field in the store to the provided stories.
   * This will replace any existing stories in the store.
   * @param stories - An array of news stories to add.
   */
  addAllStories: (stories: NewsFeedItem[]) => void;

  /**
   * Adds multiple news stories of a particular source to the store.
   * @param stories - The stories to add.
   * @param backendSource - The source of the stories.
   */
  addTypeStories: (
    stories: NewsFeedItem[],
    backendSources: BackendSource[]
  ) => void;

  addTypeStory: (story: NewsFeedItem, backendSources: BackendSource[]) => void;

  /**
   * Clears all existing stories from the store.
   */
  clearAllStories: () => void;
};

/** Initial state of the FirehoseStore */
const initialState = {
  status: FETCH_STATUS.IDLE,
  allStories: [],
  flyStories: [],
  benzingaStories: [],
  cryptoWebStories: [],
  filterSources: [],
  energyWebStories: [],
  energyXStories: [],
  truthSocialStories: [],
  stocksNewsStories: [],
  finXStories: [],
  generalWebStories: [],
  tradeXchangeStories: [],
  secFilingsStories: [],
  financialReportsEUStories: [],
};

/**
 * Zustand store for managing news stories.
 * Provides methods to add and clear stories.
 */
const useFirehoseStore = create<FirehoseStore>((set) => ({
  ...initialState,

  addStory: (story: NewsFeedItem) => {
    set((state) => {
      // Deduplicate — skip if story ID already exists
      if (state.allStories.some((s) => s.id === story.id)) {
        return state;
      }

      const key = sourceToFieldMap[story.backend_source];
      const newAll = [story, ...state.allStories];
      const newSource = [story, ...(state[key] as NewsFeedItem[])];

      return {
        allStories: newAll.length > MAX_ALL_STORIES ? newAll.slice(0, MAX_ALL_STORIES) : newAll,
        [key]: newSource.length > MAX_PER_SOURCE ? newSource.slice(0, MAX_PER_SOURCE) : newSource,
      };
    });
  },

  addAllStories: (stories: NewsFeedItem[]) => {
    set({
      allStories: stories,
    });
  },

  pushStories: (stories: NewsFeedItem[]) => {
    set((state) => {
      const newAll = [...state.allStories, ...stories];
      return {
        allStories: newAll.length > MAX_ALL_STORIES ? newAll.slice(0, MAX_ALL_STORIES) : newAll,
      };
    });
  },

  addTypeStory: (story: NewsFeedItem, backendSources: BackendSource[]) => {
    set((state) => {
      const updates: Record<string, NewsFeedItem[]> = {};

      new Set(backendSources).forEach((source) => {
        const key = sourceToFieldMap[source];
        const newArr = [story, ...(state[key] as NewsFeedItem[])];
        updates[key] = newArr.length > MAX_PER_SOURCE ? newArr.slice(0, MAX_PER_SOURCE) : newArr;
      });

      return {
        ...updates,
      };
    });
  },

  addTypeStories: (
    stories: NewsFeedItem[],
    backendSources: BackendSource[]
  ) => {
    set(() => {
      const updates: Record<string, NewsFeedItem[]> = {};

      new Set(backendSources).forEach((source) => {
        const key = sourceToFieldMap[source];
        updates[key] = [...stories];
      });

      return updates;
    });
  },

  /**
   * Clears allStories field from the store
   */
  clearAllStories: () => {
    set(() => ({
      allStories: [...initialState.allStories],
    }));
  },

  setStatus: (status: FETCH_STATUS) => {
    set({
      status,
    });
  },
}));

export default useFirehoseStore;
